import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import useBoothStatusButton from '~/features/circle-participation/hooks/useBoothStatusButton'
import { type CircleApplyDetailResponse } from '~/features/circle-participation/types/circle-apply'
import { type Goods } from '~/features/circle-participation/utils/type'
import Tag from '~/shared/components/tags/Tag'
import theme from '~/shared/styles/theme'

type Props = {
  data: CircleApplyDetailResponse
}

function CircleOptionInfo({ data }: Props) {
  const navigate = useNavigate()
  const {
    id,
    near_booth,
    image_info,
    size_type,
    complexity_type,
    exp_type,
    goods_type,
    genre_type,
    tag,
    user_nickname,
    introduce,
    homepage,
    goods_list,
    size_type_list,
    complexity_type_list,
    exp_type_list,
    goods_type_list,
    genre_type_list,
    booth_status,
  } = data.userBoothApplication

  const { addInfoModify } = useBoothStatusButton(booth_status)

  const url = image_info?.url

  const filteredSizeType =
    size_type_list?.find((item) => item.code === size_type)?.description || '-'

  const filteredComplexityType =
    complexity_type_list?.find((item) => item.code === complexity_type)
      ?.description || '-'

  const filteredExpType =
    exp_type_list?.find((item) => item.code === exp_type)?.description || '-'

  const filteredGoodsType =
    goods_type
      ?.split(',')
      .map(
        (type) =>
          goods_type_list.find((item) => item.code === type)?.description,
      )
      .join(' · ') || '-'

  const filteredGenreType =
    genre_type
      ?.split(',')
      .map(
        (type) =>
          genre_type_list.find((item) => item.code === type)?.description,
      )
      .join(' · ') || '-'

  const handleOpenNewTab = (src?: string) => {
    window.open(src, '_black', 'noopener noreferrer')
  }

  return (
    <Wrapper>
      <SubTitleBox>
        <SubTitle>서클 추가 정보</SubTitle>
        {addInfoModify && (
          <Button onClick={() => navigate(`/circle-participation/add/${id}`)}>
            추가정보 수정
          </Button>
        )}
      </SubTitleBox>
      <TagContainer>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            인접 희망 서클 이름
          </Tag>
          <TagText>{near_booth || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            대표 이미지
          </Tag>
          {!url ? (
            <TagText>-</TagText>
          ) : (
            <ImageBox
              src={url}
              alt="대표 이미지"
              onClick={() => handleOpenNewTab(url)}
            />
          )}
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            서클 규모
          </Tag>
          <TagText>{filteredSizeType}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            예상 인파
          </Tag>
          <TagText>{filteredComplexityType}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            동인행사 서클참가 경험
          </Tag>
          <TagText>{filteredExpType}</TagText>
        </TagBox>
        <TagBox className="align-start">
          <Tag $size={'sm'} $variant={'gray'}>
            취급 굿즈
          </Tag>
          <TagText>{filteredGoodsType}</TagText>
        </TagBox>
        <TagBox className="align-start">
          <Tag $size={'sm'} $variant={'gray'}>
            장르 대분류
          </Tag>
          <TagText>{filteredGenreType}</TagText>
        </TagBox>
        <TagBox className="align-start">
          <Tag $size={'sm'} $variant={'gray'}>
            세부 장르 태그
          </Tag>
          <TagText>{tag || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            대표자 닉네임
          </Tag>
          <TagText>{user_nickname || '-'}</TagText>
        </TagBox>
        <TagBox className="align-start">
          <Tag $size={'sm'} $variant={'gray'}>
            서클 소개
          </Tag>
          <IntroduceDesc>{introduce || '-'}</IntroduceDesc>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            홍보 페이지 URL
          </Tag>
          <TagText>{homepage || '-'}</TagText>
        </TagBox>
        <TagBox>
          <Tag $size={'sm'} $variant={'gray'}>
            부스 인포
          </Tag>
          {!goods_list
            ? '-'
            : goods_list?.map((goods: Goods) => (
                <ImageBox
                  key={goods.id}
                  src={goods.url}
                  alt="부스 인포"
                  onClick={() => handleOpenNewTab(goods.url)}
                />
              ))}
        </TagBox>
      </TagContainer>
    </Wrapper>
  )
}

export default CircleOptionInfo

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: flex-start;

  box-sizing: border-box;
  width: 100%;
  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  @media ${theme.device.medium} {
    padding: 24px 20px;
  }
`

const SubTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const SubTitle = styled.h2`
  width: 100%;

  ${theme.fontStyle.subTitle};

  @media ${theme.device.medium} {
    ${theme.fontStyle.subTitleMobile};
    width: 100%;
    height: auto;
  }
`

const Button = styled.button`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  height: 58px;
  padding: 0 20px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: white;
  word-wrap: break-word;
  white-space: nowrap;

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 54px;

  @media ${theme.device.large} {
    width: auto;
  }

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const TagBox = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;
  width: 100%;

  &.align-start {
    align-items: start;
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const TagText = styled.span`
  width: 100%;

  ${theme.fontStyle.text};
  color: #313634;
  word-break: break-word;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  @media ${theme.device.medium} {
    ${theme.fontStyle.textMobile};
  }
`

const IntroduceDesc = styled.p`
  width: 100%;
  font-style: ${theme.fontStyle.text};
  white-space: pre-wrap;

  @media ${theme.device.medium} {
    font-style: ${theme.fontStyle.textMobile};
  }
`

const ImageBox = styled.img`
  cursor: pointer;

  width: 132px;
  height: 132px;

  object-fit: cover;
  border-radius: 12px;
`
